import { useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Badge, Button, EmptyState, Select } from "../../../components/ui";
import { useCentinelaTestNotification } from "../../../features/centinela/mutations";
import type { NotificationUser } from "../types";

const TESTABLE_CHANNELS = ["notificacion_push", "email", "sms"];

const PHONE_CHANNELS = ["sms"];

export interface TestNotificationModalProps {
  users: NotificationUser[];
  onClose: () => void;
}

type SendStatus = "idle" | "sending" | "success" | "error";

export default function TestNotificationModal({ users, onClose }: TestNotificationModalProps) {
  const { t } = useTranslation("common");

  const testableOf = (user: NotificationUser) =>
    user.channels.filter(
      (channel) => channel.available && TESTABLE_CHANNELS.includes(channel.id),
    );

  const candidates = users.filter((user) => user.active && testableOf(user).length > 0);
  const [userId, setUserId] = useState(candidates[0]?.userId ?? "");
  const [selected, setSelected] = useState<string[]>([]);
  const [status, setStatus] = useState<SendStatus>("idle");

  const testNotification = useCentinelaTestNotification();
  const user = candidates.find((row) => row.userId === userId) ?? null;
  const available = user ? testableOf(user) : [];

  const pickUser = (nextId: string) => {
    setUserId(nextId);
    setSelected([]);
    setStatus("idle");
  };

  const toggleChannel = (channelId: string) => {
    setStatus("idle");
    setSelected((current) =>
      current.includes(channelId)
        ? current.filter((item) => item !== channelId)
        : [...current, channelId],
    );
  };

  const needsPhone = selected.some((channel) => PHONE_CHANNELS.includes(channel));
  const canSend = Boolean(user) && selected.length > 0 && (!needsPhone || Boolean(user?.phone));

  const send = () => {
    if (!user || !canSend) return;

    setStatus("sending");

    testNotification
      .mutateAsync({
        canales: selected,
        user_id: user.userId,
        ...(needsPhone && user.phone ? { telefono: user.phone } : {}),
      })
      .then(() => setStatus("success"))
      .catch(() => setStatus("error"));
  };

  return (
    <Modal
      title={t("monitoreoIa.testNotification.title")}
      onClose={onClose}
      width="w-full max-w-md"
      content={
        candidates.length === 0 ? (
          <EmptyState
            icon="ri-notification-off-line"
            title={t("monitoreoIa.testNotification.noUsers")}
          />
        ) : (
          <div className="space-y-4">
            <p className="text-xs leading-relaxed text-content-secondary">
              {t("monitoreoIa.testNotification.intro")}
            </p>

            <Select
              size="sm"
              label={t("monitoreoIa.testNotification.userLabel")}
              value={userId}
              onChange={(event) => pickUser(event.target.value)}
              options={candidates.map((row) => ({ value: row.userId, label: row.name }))}
            />

            <div className="space-y-2">
              <p className="text-xs font-medium text-content">
                {t("monitoreoIa.testNotification.channelsLabel")}
              </p>

              <div className="flex flex-wrap gap-1.5">
                {available.map((channel) => {
                  const isOn = selected.includes(channel.id);

                  return (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() => toggleChannel(channel.id)}
                      className={`rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        isOn
                          ? "border-primary-500 bg-primary-500 text-on-primary"
                          : "border-line bg-app text-content-secondary hover:bg-subtle"
                      }`}
                    >
                      {channel.label}
                    </button>
                  );
                })}
              </div>

              {user?.noPhoneReason ? (
                <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-2 text-[11px] leading-relaxed text-warning">
                  <i className="ri-alert-line mt-0.5 shrink-0 text-xs" />
                  {t("monitoreoIa.testNotification.noPhone")}
                </p>
              ) : null}
            </div>

            {status === "success" ? (
              <p className="flex items-start gap-2 rounded-xl border border-success/30 bg-success/10 px-3 py-2 text-[11px] leading-relaxed text-success">
                <i className="ri-check-line mt-0.5 shrink-0 text-xs" />
                {t("monitoreoIa.testNotification.sent")}
              </p>
            ) : null}

            {status === "error" ? (
              <p className="flex items-start gap-2 rounded-xl border border-error/30 bg-error/10 px-3 py-2 text-[11px] leading-relaxed text-error">
                <i className="ri-close-line mt-0.5 shrink-0 text-xs" />
                {t("monitoreoIa.testNotification.error")}
              </p>
            ) : null}

            <Badge tone="neutral" size="sm">
              {t("monitoreoIa.testNotification.sameContent")}
            </Badge>

            <Button
              fullWidth
              size="sm"
              disabled={!canSend || status === "sending"}
              loading={status === "sending"}
              onClick={send}
              icon={<i className="ri-send-plane-line" />}
            >
              {t("monitoreoIa.testNotification.send")}
            </Button>
          </div>
        )
      }
    />
  );
}
